import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Brand, BrandRequest, Company, CreateBrandRequest, QualityLevel, SituViewWebApiClient } from 'situview-webapi-client';
import { CompanyViewService } from 'src/app/services';

// type Formify<T> = {
//   [K in keyof T]?: FormControl;
// };

type NewBrand = FormGroup & { filteredSuppliers: Observable<Company[]>, companyName: FormControl, logoId?: string, logoUri: string };

export interface CreateBrandDialogData {
  brands?: string[];
  companyId?: string;
}


@Component({
  selector: 'create-brand-dialog',
  templateUrl: './create-brand-dialog.component.html',
  styleUrls: ['./create-brand-dialog.component.scss']
})
export class CreateBrandDialogComponent implements OnInit {
  public newBrandForms = new FormArray([]);
  public allCompanies: Company[] = [];
  public readonly QualityLevel = QualityLevel;

  constructor(
    private readonly situViewApi: SituViewWebApiClient,
    private companyViewService: CompanyViewService,
    private snackbar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: CreateBrandDialogData,
    private dialogRef: MatDialogRef<CreateBrandDialogComponent>
  ) {
    // load companies
    companyViewService.LoadCompanies().then((companies) => {
      this.allCompanies = companies;

      // assign company names if they missed out earlier
      this.newBrandForms.controls.forEach((control) => {
        let newBrand = control as NewBrand;
        newBrand.companyName.setValue(companies.find(c => c.id === newBrand.controls.companyId.value)?.name);
      });

    })

    // create from prompt
    if (this.data?.brands && this.data.brands.length > 0) {
      this.data.brands.forEach(brandName =>
        this.newBrandForms.push(this.createBrandForm(brandName, this.data?.companyId)));
    } else {
      this.onAddBrand();
    }

  }

  ngOnInit(): void {
  }

  private createBrandForm(name?: string, companyId?: string): NewBrand {

    let newBrand: NewBrand = new FormGroup({
      name: new FormControl(name || '', [Validators.required, Validators.maxLength(255)]),
      companyId: new FormControl(companyId || ''),
      qualityLevel: new FormControl(null),
      showBrandLogoOnly: new FormControl(false),
    }) as NewBrand;

    let company = this.allCompanies.find(c => c.id === companyId);
    newBrand.companyName = new FormControl({ value: company?.name, disabled: !!companyId });
    newBrand.filteredSuppliers = newBrand.companyName.valueChanges.pipe(
      startWith(null),
      map((input) => {
        if (!input) {
          return this.allCompanies;
        }
        return this.allCompanies.filter((c => c.name?.toLowerCase().includes(input.toLowerCase())));
      })
    );
    return newBrand
  }

  public onSupplierSelected(event: MatAutocompleteSelectedEvent, newBrand: NewBrand): void {
    let supplier = this.allCompanies.find(c => c.name === event.option.value);
    if (supplier) {
      newBrand.controls.companyId.setValue(supplier.id);
    }
  }

  public clearCompanyField(newBrand: NewBrand): void {
    newBrand.companyName.reset();
    newBrand.controls.companyId.reset();
  }

  public onSaveBrands(): void {
    const brands = (this.newBrandForms.controls as NewBrand[]).map(c => {
      return {
        ...c.getRawValue(),
        brandLogoUrl: c.logoUri,
        logoFileVaultId: c.logoId,
      } as BrandRequest;
    });
    Promise.all(brands.map((brand: Brand) => this.situViewApi.createBrand({ brand: brand } as CreateBrandRequest))).then((responses) => {
      if (responses.every(r => r.success)) {
        const createdBrands = responses.map(r => r.brand);
        this.snackbar.open(`Brand${brands.length > 1 ? 's' : ''} ${createdBrands.map(b => b?.name).join(", ")} created`);
        this.dialogRef.close(true);
      } else {
        responses.forEach((r, index) => {
          // remove the brands that were able to be created. Index is preserved from promise.all
          if (r.success) {
            this.newBrandForms.removeAt(index);
          }
        });
        this.snackbar.open(`Failed to create ${this.newBrandForms.controls.length > 1 ? 'some brands' : 'brand'}:
           ${this.newBrandForms.controls.map((b) => (b as NewBrand).controls.name.value).join(", ")}`);
      }
    },
      // promise rejected
      (reason) => this.snackbar.open(`Failed to create ${this.newBrandForms.controls.length > 1 ? 'some brands' : 'brand'}. Please refresh the page`));
  }

  public onAddBrand(): void {
    this.newBrandForms.push(this.createBrandForm(undefined, this.data?.companyId));
  }
}
